import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Sankey } from '@nivo/sankey';
import { Calendar, TrendingUp, TrendingDown, BarChart2, PieChart as PieChartIcon, Activity } from 'lucide-react';

const COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'];

export const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState('week');

  const performanceData = [
    { name: 'Mon', efficiency: 85, recovery: 75, energy: 95 },
    { name: 'Tue', efficiency: 88, recovery: 78, energy: 92 },
    { name: 'Wed', efficiency: 87, recovery: 76, energy: 89 },
    { name: 'Thu', efficiency: 84, recovery: 74, energy: 91 },
    { name: 'Fri', efficiency: 90, recovery: 79, energy: 94 },
    { name: 'Sat', efficiency: 89, recovery: 77, energy: 93 },
    { name: 'Sun', efficiency: 86, recovery: 75, energy: 90 },
  ];

  const resourceUsage = [
    { name: 'Energy', value: 35 },
    { name: 'Water', value: 25 },
    { name: 'Chemicals', value: 20 },
    { name: 'Maintenance', value: 20 },
  ];

  const sankeyData = {
    nodes: [
      { id: 'input', color: '#3b82f6' },
      { id: 'pretreatment', color: '#60a5fa' },
      { id: 'ro', color: '#93c5fd' },
      { id: 'product', color: '#34d399' },
      { id: 'waste', color: '#f87171' },
    ],
    links: [
      { source: 'input', target: 'pretreatment', value: 100 },
      { source: 'pretreatment', target: 'ro', value: 90 },
      { source: 'ro', target: 'product', value: 75 },
      { source: 'ro', target: 'waste', value: 15 },
      { source: 'pretreatment', target: 'waste', value: 10 },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Performance Analytics</h2>
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="day">Last 24 Hours</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="year">Last Year</option>
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-900">System Efficiency</h3>
              <TrendingUp className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-900">87.5%</p>
            <p className="text-sm text-blue-600">+2.5% from last week</p>
          </div>
          <div className="bg-green-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">Recovery Rate</h3>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-900">76.3%</p>
            <p className="text-sm text-green-600">+1.8% from last week</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-yellow-900">Energy Usage</h3>
              <TrendingDown className="h-5 w-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-yellow-900">92.1%</p>
            <p className="text-sm text-yellow-600">-0.5% from last week</p>
          </div>
        </div>

        {/* Performance Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="efficiency" name="System Efficiency" fill="#3b82f6" />
              <Bar dataKey="recovery" name="Recovery Rate" fill="#34d399" />
              <Bar dataKey="energy" name="Energy Efficiency" fill="#fbbf24" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Resource Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Resource Distribution</h3>
            <PieChartIcon className="h-5 w-5 text-gray-500" />
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={resourceUsage}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {resourceUsage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Process Flow Analysis</h3>
            <Activity className="h-5 w-5 text-gray-500" />
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <div style={{ width: '100%', height: '100%' }}>
                <Sankey
                  data={sankeyData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  width={500}
                  height={300}
                  nodeWidth={15}
                  nodePadding={10}
                  nodeOpacity={1}
                  nodeHoverOthersOpacity={0.35}
                  linkOpacity={0.3}
                  linkHoverOthersOpacity={0.1}
                  enableLinkGradient={true}
                  labelPosition="outside"
                />
              </div>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};